import {
  Component,
  NgZone,
  OnInit,
  Signal,
  ViewChild,
  ViewChildren,
  QueryList,
  computed,
  inject,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  TemplateRef,
} from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injector } from '@angular/core';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';

import { SidebarService } from './data-access/sidebar.service';
import { Observable } from 'rxjs';
import { HeaderComponent } from './features/header/header.component';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { TooltipDirective } from '../../shared/directives/tooltip/tooltip.directive';
import { SidebarItem, SidebarComponentItem } from './sidebar-types';
import { SidebarItemComponent } from './components/sidebar-page/sidebar-page.component';
import { animate } from 'animejs';
import { map } from 'rxjs/operators';
import { HeaderService } from './features/header/data-access/header.service';
import { SidebarContentComponent } from './sidebar-content.component';

@Component({
  selector: 'sidebar-overlay',
  templateUrl: './sidebar-overlay.component.html',
  styleUrls: ['./sidebar-overlay.component.css'],
  imports: [
    RouterModule,
    HeaderComponent,
    CommonModule,
    TooltipDirective,
    SidebarItemComponent,
  ],
  host: {
    class: 'absolute right-0 top-0 bottom-0 z-[500]'
  }
})
export class SidebarOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('sideNav', { static: true }) drawer: ElementRef;
  @ViewChildren(SidebarItemComponent) sidebarItems!: QueryList<SidebarItemComponent>;

  private overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;
  private animation: any = null;
  private isInitialized = false; // Flag to prevent opening during initialization
  private sidebarStateSubscription: any = null;

  protected readonly breakpointObserver: BreakpointObserver =
    inject(BreakpointObserver);

  isSmallScreen$: Observable<boolean>;
  isSmallScreen: Signal<boolean>;
  display;

  constructor(
    private zone: NgZone,
    private router: Router,
    public route: ActivatedRoute,
    public sidebarService: SidebarService,
    private injector: Injector
  ) {
    this.isSmallScreen$ = this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(map((state: BreakpointState) => state.matches));

    this.isSmallScreen = toSignal(this.isSmallScreen$, { initialValue: false });

    this.display = computed(() => {
      return this.isSmallScreen() ? 'none' : 'grid';
    });
  }

  ngOnInit(): void {
    this.setupOverlay();
    this.setupSidebarStateListener();
  }

  ngAfterViewInit(): void {
    // Set the drawer reference after view is initialized
    this.sidebarService.setDrawer(this.drawer);

    // The sidebar items are now self-contained and handle their own registration
    // Set initialized flag after a short delay to allow all components to register
    setTimeout(() => {
      this.isInitialized = true;
      this.sidebarService.drawerReady$.next(true);
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroyOverlay();
    if (this.sidebarStateSubscription) {
      this.sidebarStateSubscription.unsubscribe();
    }
  }

  private setupOverlay(): void {
    // Create overlay position strategy - position on the right side of the screen
    const positionStrategy = this.overlay.position()
      .global()
      .right('0px')
      .top('0px');

    // Create overlay config
    const overlayConfig = {
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false, // Removed backdrop to allow map interaction
      panelClass: 'sidebar-overlay-panel',
    };

    // Create overlay reference
    this.overlayRef = this.overlay.create(overlayConfig);

    // Remove backdrop click handler - no backdrop click to close
  }

  private destroyOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    if (this.animation) {
      this.animation.pause();
      this.animation = null;
    }
  }

  private setupSidebarStateListener(): void {
    // Listen to the sidebar service's opened$ observable
    this.sidebarStateSubscription = this.sidebarService.opened$.subscribe((isOpen) => {
      if (this.isInitialized) {
        if (isOpen) {
          this.openSidebar();
        } else {
          this.closeSidebar();
        }
      }
    });
  }

  openSidebar(): void {


    if (!this.overlayRef || this.overlayRef.hasAttached()) {

      return;
    }

    const portal = new ComponentPortal(
      SidebarContentComponent,
      null, // viewContainerRef
      this.createOverlayInjector() // custom injector
    );
    const componentRef = this.overlayRef.attach(portal);


    componentRef.instance.sidebarService = this.sidebarService;
    componentRef.instance.getActiveComponentItems = this.getActiveComponentItems.bind(this);
    componentRef.instance.closeSidebar = this.closeSidebar.bind(this);
    componentRef.instance.getContentTemplate = this.sidebarService.getContentTemplate.bind(this.sidebarService);

    this.animateSidebarIn();
  }

  closeSidebar(): void {
    if (!this.overlayRef || !this.overlayRef.hasAttached()) {
      return;
    }

    this.animateSidebarOut(() => {
      this.overlayRef?.detach();
    });
  }

  private animateSidebarIn(): void {
    const overlayElement = this.overlayRef?.overlayElement;
    if (!overlayElement) return;

    // Set initial state
    overlayElement.style.transform = 'translateX(100%)';
    overlayElement.style.opacity = '0';

    // Animate in
    this.animation = animate(overlayElement, {
      translateX: ['100%', '0%'],
      opacity: [0, 1],
      duration: 300,
      ease: 'out(3)',
    });
  }

  private animateSidebarOut(callback?: () => void): void {
    const overlayElement = this.overlayRef?.overlayElement;
    if (!overlayElement) {
      return;
    }

    // Animate out
    this.animation = animate(overlayElement, {
      translateX: ['0%', '100%'],
      opacity: [1, 0],
      duration: 300,
      ease: 'in(3)',
      onComplete: () => {
        if (callback) callback();
      },
    });
  }

  // Handle clicks on sidebar items from the service pages
  handlePageClick(page: SidebarItem): void {
    if (this.isComponentItem(page)) {
      this.sidebarService.toggleComponentItem(page.id);
      // The overlay will be opened/closed automatically via the sidebar service state listener
    } else {
      // For route-based items, navigate to the route
      if (page.route) {
        this.router.navigate([page.route], { relativeTo: this.route });
      }
    }
  }

  // Handle clicks on sidebar items directly
  handleSidebarItemClick(item: SidebarItemComponent): void {
    item.handleClick();
  }

  // Check if there are any active component items
  hasActiveComponentItem(): boolean {
    return this.sidebarService.pages().some(item =>
      this.isComponentItem(item) && item.active
    );
  }

  // Get all active component items
  getActiveComponentItems(): SidebarComponentItem[] {
    return this.sidebarService.pages()
      .filter(item => this.isComponentItem(item) && item.active)
      .map(item => item as SidebarComponentItem);
  }

  // Get sidebar items for rendering
  getSidebarItems(): SidebarItemComponent[] {
    return this.sidebarItems?.toArray() || [];
  }

  private isComponentItem(page: SidebarItem): page is SidebarComponentItem {
    return 'id' in page && 'component' in page;
  }

  private createOverlayInjector(): Injector {
    return Injector.create({
      providers: [
        { provide: HeaderService, useClass: HeaderService },
        { provide: SidebarService, useValue: this.sidebarService }
      ],
      parent: this.injector
    });
  }

  getSidebarIconTemplate(id: string): TemplateRef<any> | null {
    return this.sidebarService.getSidebarIconTemplate(id);
  }
}
